import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { X, Loader2, Send, Check, ArrowDown } from 'lucide-react';
import { toast } from '../utils/toast';
const API = window.__API_BASE + '/api';

/**
 * MJ Iteration Modal — Premium feature for iterating keyframes via Midjourney.
 * Shows original 4 MJ grid images + stacked iteration rounds.
 * Each round: 4 new images + the feedback that produced them.
 * User can select any image from any round → "Move to Panel".
 */
export default function MjIterationModal({ shotId, shot, onClose, onSelect, aspectClass = 'aspect-video' }) {
  const [iterations, setIterations] = useState([]);
  const [originalGrid, setOriginalGrid] = useState([]);
  const [loading, setLoading] = useState(true);
  const [iterating, setIterating] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [selectedImage, setSelectedImage] = useState(null); // { round, index, url }
  const [currentSelectedRound, setCurrentSelectedRound] = useState(null);
  const [currentSelectedIndex, setCurrentSelectedIndex] = useState(null);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);

  const fetchIterations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/shots/${shotId}/mj-iterations`);
      setOriginalGrid(res.data.original_grid || []);
      setIterations(res.data.iterations || []);
      setIterating(res.data.iterating || false);
      if (res.data.error) setError(res.data.error);
      setCurrentSelectedRound(res.data.selected_round ?? -1);
      setCurrentSelectedIndex(res.data.selected_index ?? 0);
    } catch (err) {
      toast.error('Failed to load iterations');
    } finally {
      setLoading(false);
    }
  }, [shotId]);

  useEffect(() => {
    fetchIterations();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchIterations]);

  // Poll while iterating
  useEffect(() => {
    if (iterating) {
      pollRef.current = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/shots/${shotId}/mj-iterations`);
          const newIterating = res.data.iterating || false;
          setIterations(res.data.iterations || []);
          setIterating(newIterating);
          if (res.data.error) {
            setError(res.data.error);
            setIterating(false);
          }
          if (!newIterating && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            // Scroll to top to show new results
            if (scrollRef.current) scrollRef.current.scrollTop = 0;
          }
        } catch { /* silent */ }
      }, 4000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [iterating, shotId]);

  const handleIterate = async () => {
    if (!feedback.trim()) return;
    setError('');
    setIterating(true);
    try {
      await axios.post(`${API}/shots/${shotId}/iterate-mj`, { feedback: feedback.trim() });
      setFeedback('');
      toast.info('MJ iteration started — generating 4 new options...');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to start MJ iteration';
      setError(msg);
      setIterating(false);
      toast.error(msg);
    }
  };

  const handleSelect = async () => {
    if (!selectedImage) return;
    try {
      const res = await axios.post(`${API}/shots/${shotId}/select-mj-iteration`, {
        round: selectedImage.round,
        index: selectedImage.index,
      });
      toast.success(res.data.message || 'Image selected as keyframe');
      onSelect?.(res.data.keyframe_url);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Selection failed');
    }
  };

  const isSelected = (round, idx) => selectedImage?.round === round && selectedImage?.index === idx;
  const isCurrentKeyframe = (round, idx) => currentSelectedRound === round && currentSelectedIndex === idx;

  // Render a 4-image grid row
  const renderGrid = (gridUrls, roundIdx, label, feedbackText) => {
    if (!gridUrls || gridUrls.length === 0) return null;
    return (
      <div className="mb-4" data-testid={`mj-iter-round-${roundIdx}`}>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-white/50">{label}</span>
          {feedbackText && (
            <span className="text-[10px] text-purple-300/80 italic truncate max-w-[60%]" title={feedbackText}>
              "{feedbackText}"
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2">
          {gridUrls.map((url, idx) => (
            <button
              key={`${roundIdx}-${idx}`}
              onClick={() => setSelectedImage({ round: roundIdx, index: idx, url })}
              className={`relative rounded-lg overflow-hidden border-2 transition-all ${aspectClass} ${
                isSelected(roundIdx, idx)
                  ? 'border-[#FF9500] shadow-lg shadow-[#FF9500]/30 scale-[1.02]'
                  : isCurrentKeyframe(roundIdx, idx)
                    ? 'border-purple-500/60'
                    : 'border-white/10 hover:border-white/30'
              }`}
              data-testid={`mj-iter-img-${roundIdx}-${idx}`}
            >
              <img src={url} alt={`Option ${idx + 1}`} className="w-full h-full object-cover" loading="lazy" />
              {isSelected(roundIdx, idx) && (
                <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-[#FF9500] flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
              {isCurrentKeyframe(roundIdx, idx) && !isSelected(roundIdx, idx) && (
                <div className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded text-[7px] font-bold bg-purple-500/80 text-white">
                  CURRENT
                </div>
              )}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-1">
                <span className="text-[9px] font-medium text-white/80">Option {idx + 1}</span>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  };

  const content = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 md:p-8"
      style={{ background: 'rgba(0,0,0,0.92)' }}
      onClick={onClose}
      data-testid="mj-iteration-modal"
    >
      <div
        className="relative w-full max-w-4xl max-h-[90vh] rounded-2xl overflow-hidden flex flex-col"
        style={{ background: '#0d0618', border: '1px solid rgba(168, 85, 247, 0.25)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/10 flex-shrink-0">
          <div>
            <h2 className="text-sm font-bold text-white">Midjourney Iteration Workbench</h2>
            <p className="text-[10px] text-white/40 mt-0.5">
              {shot?.scene_number} — {shot?.shot_number} · {iterations.length} iteration{iterations.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 transition-colors" data-testid="mj-iter-close">
            <X className="w-5 h-5 text-white/60" />
          </button>
        </div>

        {/* Body: Two columns — images left, feedback right */}
        <div className="flex-1 flex overflow-hidden min-h-0">
          {/* Left: Scrollable iteration grids */}
          <div className="flex-1 overflow-y-auto p-4 space-y-2" ref={scrollRef} data-testid="mj-iter-scroll">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
              </div>
            ) : (
              <>
                {/* Iterating spinner at top */}
                {iterating && (
                  <div className="flex items-center gap-2 p-3 rounded-xl border border-[#FF9500]/30 mb-4" style={{ background: 'rgba(255,149,0,0.08)' }}>
                    <Loader2 className="w-4 h-4 text-[#FF9500] animate-spin flex-shrink-0" />
                    <span className="text-[11px] text-[#FF9500] font-medium">Generating 4 new options via Midjourney...</span>
                  </div>
                )}

                {/* Iterations — newest first */}
                {[...iterations].reverse().map((iter, revIdx) => {
                  const actualIdx = iterations.length - 1 - revIdx;
                  return renderGrid(
                    iter.grid_urls,
                    actualIdx,
                    `Round ${actualIdx + 1}`,
                    iter.feedback
                  );
                })}

                {/* Separator */}
                {iterations.length > 0 && originalGrid.length > 0 && (
                  <div className="flex items-center gap-3 py-2">
                    <div className="flex-1 h-px bg-white/10" />
                    <ArrowDown className="w-3 h-3 text-white/30" />
                    <span className="text-[9px] text-white/30 uppercase tracking-wider">Original Generation</span>
                    <ArrowDown className="w-3 h-3 text-white/30" />
                    <div className="flex-1 h-px bg-white/10" />
                  </div>
                )}

                {/* Original grid */}
                {renderGrid(originalGrid, -1, 'Original', null)}

                {originalGrid.length === 0 && iterations.length === 0 && (
                  <div className="text-center py-20 text-white/30 text-sm">No MJ images available for this shot.</div>
                )}
              </>
            )}
          </div>

          {/* Right: Feedback panel */}
          <div className="w-72 border-l border-white/10 flex flex-col flex-shrink-0" style={{ background: 'rgba(10,4,20,0.6)' }}>
            {/* Error display */}
            {error && (
              <div className="px-4 pt-3">
                <div className="p-2.5 rounded-lg text-[10px] text-red-400 border border-red-500/20" style={{ background: 'rgba(239,68,68,0.08)' }}>
                  {error}
                </div>
              </div>
            )}

            {/* Feedback input area */}
            <div className="flex-1 flex flex-col justify-end p-4">
              <div className="mb-3">
                <label className="text-[10px] font-medium text-white/50 uppercase tracking-wider mb-2 block">
                  Director's Feedback
                </label>
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="e.g., 'Make the jacket darker' or 'Change to a low angle shot'..."
                  className="w-full h-28 text-xs px-3 py-2.5 text-white rounded-xl border border-white/10 outline-none resize-none focus:border-purple-500/50 transition-colors"
                  style={{ background: 'rgba(255,255,255,0.04)' }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && feedback.trim()) { e.preventDefault(); handleIterate(); } }}
                  disabled={iterating}
                  data-testid="mj-iter-feedback-input"
                />
              </div>
              <button
                onClick={handleIterate}
                disabled={iterating || !feedback.trim()}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-semibold transition-all disabled:opacity-30"
                style={{ background: iterating ? 'rgba(168,85,247,0.2)' : 'linear-gradient(135deg, #A855F7, #7C3AED)', color: 'white' }}
                data-testid="mj-iter-submit-btn"
              >
                {iterating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                {iterating ? 'Generating...' : 'Iterate with MJ'}
              </button>
            </div>

            {/* Selected image preview + Move to Panel */}
            {selectedImage && (
              <div className="border-t border-white/10 p-4" data-testid="mj-iter-selection-panel">
                <div className="flex items-center gap-2 mb-2">
                  <img src={selectedImage.url} alt="Selected" className="w-12 h-8 rounded object-cover border border-[#FF9500]/50" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-medium text-white truncate">
                      {selectedImage.round === -1 ? 'Original' : `Round ${selectedImage.round + 1}`} — Option {selectedImage.index + 1}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleSelect}
                  className="w-full py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5"
                  style={{ background: 'linear-gradient(135deg, #FF9500, #FF6B00)', color: 'white' }}
                  data-testid="mj-iter-move-to-panel-btn"
                >
                  <Check className="w-3.5 h-3.5" />
                  Move to Panel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? ReactDOM.createPortal(content, document.body) : null;
}
